import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from 'react-bootstrap';
import { FiSliders, FiMapPin, FiFlag, FiCalendar, FiClock, FiGrid } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import GeneralTab from './generalTab';
import CenterSettingsTab from './centerSettingsTab';
import CountersTab from './countersTab';
import DateOverridesTab from './dateOverridesTab';
import CalendarTab from './calendarTab';
import SlotPreviewTab from './slotPreviewTab';
import TabGuide from './tabGuide';
import GUIDES from './guides';
import { AlertBox } from './common';
import { normalizeCenters, getErrorMessage } from './helpers';
import { SlotThemeProvider } from './fields';
import './slotSettings.css';
import './counters.css';
import './slotPreview.css';
import './theme.css';

const TABS = [
    { key: 'general', icon: <FiSliders />, title: 'General', desc: 'Rules for all centers' },
    { key: 'center', icon: <FiMapPin />, title: 'Center Settings', desc: 'Override per center' },
    { key: 'counters', icon: <FiGrid />, title: 'Counters', desc: 'Counters per center' },
    { key: 'holidays', icon: <FiFlag />, title: 'Holidays', desc: 'Holidays & special openings' },
    { key: 'calendar', icon: <FiCalendar />, title: 'Calendar', desc: 'Month view per center' },
    { key: 'slots', icon: <FiClock />, title: 'Slot Preview', desc: 'Live slots for a date' }
];

const tabFromHash = () => {
    const key = window.location.hash.replace('#', '');
    return TABS.some(tab => tab.key === key) ? key : 'general';
};

const SlotSettings = ({ history }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(tabFromHash);
    const [general, setGeneral] = useState(null);
    const [generalLoading, setGeneralLoading] = useState(true);
    const [generalError, setGeneralError] = useState('');
    const [centers, setCenters] = useState([]);
    const [centersLoading, setCentersLoading] = useState(true);
    const [centersError, setCentersError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [slotChanges, setSlotChanges] = useState([]);
    const slotChangesRef = useRef(null);

    useEffect(() => {
        if (slotChanges.length && slotChangesRef.current) {
            slotChangesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [slotChanges]);

    const handleSaved = useCallback((res) => {
        setRefreshKey(key => key + 1);
        const messages = Array.isArray(res?.slotSyncMessages) ? res.slotSyncMessages.filter(Boolean) : [];
        setSlotChanges(messages);
    }, []);

    const loadGeneral = useCallback(() => {
        setGeneralLoading(true);
        setGeneralError('');
        slotSettingActions.getGeneral().then(res => {
            setGeneral(res);
            setGeneralLoading(false);
        }).catch(err => {
            setGeneralLoading(false);
            setGeneralError(getErrorMessage(err, 'Unable to load general settings'));
        });
    }, []);

    const loadCenters = useCallback(() => {
        setCentersLoading(true);
        setCentersError('');
        slotSettingActions.getActiveCenters().then(res => {
            setCenters(normalizeCenters(res));
            setCentersLoading(false);
        }).catch(err => {
            setCentersLoading(false);
            setCentersError(getErrorMessage(err, 'Unable to load centers'));
        });
    }, []);

    useEffect(() => {
        loadGeneral();
        loadCenters();
    }, [loadGeneral, loadCenters]);

    const handleSelect = (key) => {
        setActiveTab(key);
        if (history) history.replace({ ...history.location, hash: `#${key}` });
    };

    const [counterCenter, setCounterCenter] = useState('');
    const openCounters = (centerCode) => {
        setCounterCenter(centerCode);
        handleSelect('counters');
    };

    const handleGeneralSaved = (res) => {
        setGeneral(res);
        handleSaved(res);
    };

    const centerProps = { centers, centersLoading, centersError, onRetryCenters: loadCenters };

    return (
        <SlotThemeProvider>
            <div className="container-fluid slot-settings">
                <div className="ss-header">
                    <div>
                        <h4>{t('sideNav.slotSettings')}</h4>
                        <p>Control when biometric appointment slots are open, for all centers or one center at a time.</p>
                    </div>
                </div>

                <div className="ss-tabs" role="tablist" aria-label="Slot settings sections">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            id={`ss-tab-${tab.key}`}
                            aria-selected={activeTab === tab.key}
                            className={`ss-tab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => handleSelect(tab.key)}
                        >
                            <span className="ss-tab-icon">{tab.icon}</span>
                            <span className="ss-tab-text">
                                <span className="ss-tab-title">{tab.title}</span>
                                <span className="ss-tab-desc">{tab.desc}</span>
                            </span>
                        </button>
                    ))}
                </div>

                {slotChanges.length > 0 && (
                    <div ref={slotChangesRef}>
                        <AlertBox variant="info" title="Slots were updated by your last save" messages={slotChanges}
                            onClose={() => setSlotChanges([])} />
                    </div>
                )}

                <TabGuide key={activeTab} tab={activeTab} {...GUIDES[activeTab]} />

                <Tab.Container activeKey={activeTab} mountOnEnter>
                    <Tab.Content>
                        <Tab.Pane eventKey="general" aria-labelledby="ss-tab-general">
                            <GeneralTab general={general} loading={generalLoading} loadError={generalError}
                                onRetry={loadGeneral} onSaved={handleGeneralSaved} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="center" aria-labelledby="ss-tab-center">
                            <CenterSettingsTab {...centerProps} general={general} refreshKey={refreshKey} onSaved={handleSaved}
                                onOpenCounters={openCounters} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="counters" aria-labelledby="ss-tab-counters">
                            <CountersTab {...centerProps} refreshKey={refreshKey} onSaved={handleSaved} preferredCenter={counterCenter} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="holidays" aria-labelledby="ss-tab-holidays">
                            <DateOverridesTab centers={centers} centersError={centersError} refreshKey={refreshKey} onSaved={handleSaved} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="calendar" aria-labelledby="ss-tab-calendar">
                            <CalendarTab {...centerProps} refreshKey={refreshKey} onSaved={handleSaved} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="slots" aria-labelledby="ss-tab-slots">
                            <SlotPreviewTab {...centerProps} refreshKey={refreshKey} />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </div>
        </SlotThemeProvider>
    );
};

export default SlotSettings;
